import { theme } from "@suraasa/placebo-ui-legacy"
import { StepsOverview } from "api/resources/jobs/types"
import { CoverStyle } from "api/resources/profile/types"
import { SchoolProfile } from "api/resources/schools/types"
import { PhoneNumber } from "api/resources/users/types"
import { ErrorResponse } from "api/types"
import { FieldValues, UseFormSetError } from "react-hook-form"
import { NavigateFunction } from "react-router-dom"
import { routes } from "utils/routes"

import { getAuthInfo } from "./auth"
import { NotificationAction, USER_TYPE } from "./constants"
import toast from "./toast"

/**
 * @param setter setter to set errors in the form
 * @param fieldErrors errors from the API
 * @param fields Format: [BackendKey, FrontendKey] OR [key] (if both key names are same)
 */
export const mapErrors = <T extends FieldValues>(
  setter: UseFormSetError<T>,
  err: ErrorResponse["errors"],
  fields?: ([string, string] | [string])[]
) => {
  const { fieldErrors, message } = err

  if (message) {
    toast.error(message)
  }

  if (!fields) {
    // if there was no message, but there were fieldErrors without a setter, we need to show them to the user
    if (!message && fieldErrors) {
      const firstError = Object.entries(fieldErrors)[0]
      if (firstError) {
        const [k, v] = firstError
        toast.error(`Error on key '${k}'`, {
          body: typeof v === "string" ? v : JSON.stringify(v[0]),
        })
      }
    }

    return
  }

  fields.forEach(pair => {
    if (!fieldErrors) return

    const key = pair[0]
    const message = Array.isArray(fieldErrors[key])
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore this is safe
        fieldErrors[key].join("; ")
      : fieldErrors[key]

    if (pair.length === 1) {
      if (message) {
        setter(
          key as any,
          {
            message: message as string,
          },
          { shouldFocus: true }
        )
      }
    }
    if (pair.length === 2) {
      const frontendKey = pair[1]
      if (message) {
        setter(
          frontendKey as any,
          { message: message as string },
          { shouldFocus: true }
        )
      }
    }
  })
}

export const handleErrors = <T extends FieldValues>(
  setter: UseFormSetError<T>,
  errors: ErrorResponse["errors"]
) => {
  const { message, fieldErrors } = errors
  if (message) {
    toast.error(message)
  }

  if (fieldErrors) {
    for (const [k, v] of Object.entries(fieldErrors)) {
      setter(k as any, { message: typeof v === "string" ? v : v[0] })
    }
  }
}

export const URLSearchParamsToObject = (params: URLSearchParams) => {
  const obj: Record<string, string> = {}
  for (const [key, value] of params) {
    obj[key] = value
  }
  return obj
}

export const buildParams = (rawParams: {
  [key: string]: string | string[]
}) => {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(rawParams)) {
    if (!value) continue

    if (Array.isArray(value)) {
      params.append(key, value.join())
    } else params.append(key, value)
  }

  return params
}

export function pluralize(
  word: string,
  count: number,
  {
    endsWithVowel,
    skipCount,
    plural,
  }: {
    endsWithVowel?: boolean
    skipCount?: boolean
    plural?: string
  } = {
    endsWithVowel: false,
    skipCount: false,
    plural: "",
  }
) {
  let str = `${count} `

  if (skipCount) {
    str = ""
  }

  if (plural) {
    return `${count !== 1 ? `${str}${plural}` : `${str}${word}`}`
  }

  return `${
    count !== 1 ? `${str}${word}${endsWithVowel ? "es" : "s"}` : `${str}${word}`
  }`
}

export type ValueOf<T> = T[keyof T]

export const getPlatformURL = (
  platform: "sso" | "learn" | "suraasa",
  url: string
) => {
  switch (platform) {
    case "sso":
      return `${import.meta.env.VITE_SSO_URL}${url}`
    case "learn":
      return `${import.meta.env.VITE_LEARN_PLATFORM_URL}${url}`
    case "suraasa":
      return `${import.meta.env.VITE_SURAASA_PLATFORM_URL}${url}`
    default:
      return url
  }
}

export const generateHuddleURL = (config: {
  meetingUrl: string
  displayName?: string
}) => {
  const authInfo = getAuthInfo()
  if (!authInfo) return ""

  const meetingURL = new URL(config.meetingUrl)
  meetingURL.searchParams.append(
    "displayName",
    `${authInfo.user.firstName} ${authInfo.user.lastName}`
  )

  const searchParams = new URLSearchParams()

  searchParams.append("meeting-link", encodeURIComponent(meetingURL.href))

  return `${routes.attemptInterview}?${searchParams.toString()}`
}

export const isProfileComplete = (
  overview: StepsOverview & { loading: boolean }
) => {
  if (overview.loading) return false
  return (
    overview.resumeExists &&
    overview.marketingFormDataExists &&
    overview.profileDataExists &&
    overview.qualificationDataExists
  )
}

export const generateProfileCompletionURL = (config: {
  jobId: string
  ri?: string
}) => {
  try {
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.append("applyingOnJobId", config.jobId)
    // ri means register interest
    if (config.ri) nextUrl.searchParams.append("ri", config.ri)

    if (import.meta.env.VITE_SSO_URL) {
      // const url = new URL(import.meta.env.VITE_SSO_URL)
      // url.searchParams.append("platform", USER_TYPE)
      // url.searchParams.append("next", nextUrl.href)
      // url.searchParams.append("redirect-url", window.location.origin)
      return `${
        import.meta.env.VITE_SSO_URL
      }/?platform=${USER_TYPE}&redirect-url=${encodeURIComponent(
        `${window.location.origin}?next=${encodeURIComponent(
          nextUrl.pathname + nextUrl.search
        )}`
      )}`
    }
  } catch (error) {
    console.error(error)
    return import.meta.env.VITE_SSO_URL
  }
}

export const notificationHelper = (
  navigate: NavigateFunction,
  // eslint-disable-next-line @typescript-eslint/ban-types
  action: NotificationAction | {} | null
) => {
  if (!action || !("name" in action)) return
  switch (action.name) {
    case "JOB_OFFER_RECEIVED":
      navigate(
        routes.viewOffer.replace(":jobOfferId", String(action.data.jobOfferId))
      )
      break
    case "INTERVIEW_SCHEDULED":
    case "LIVE_DEMO_SCHEDULED":
    case "SCHOOL_ASSESSMENT_SCHEDULED":
      navigate(
        `${routes.jobTimeline
          .replace(":jobId", String(action.data.jobId))
          .replace(
            ":jobApplicant",
            action.data.jobApplicant.toString()
          )}?jobApplicantStepId=${action.data.jobApplicantStepId}&tab=Timeline`
      )
      break
    case "INVITE_RECEIVED":
      navigate(
        `${routes.home}?tab=Pending+Applications&inviteId=${action.data.jobApplicantId}`
      )
      break
    case "JOB_PUBLISHED":
      navigate(
        `${routes.schoolProfile.replace(
          ":slug",
          action.data.schoolSlug
        )}?jobSlug=${action.data.jobSlug}`
      )
      break
    default:
      break
  }
}

/**
 * @returns An array of timeouts which the consumer must use to clear the timeouts in case they are not needed.
 * This is useful for preventing unusal side-effects
 */
export const highlightElement = ({
  element,
  onEnd,
}: {
  element: HTMLElement | null
  onEnd?: () => void
}) => {
  const timeouts: NodeJS.Timeout[] = []

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" })
    timeouts.push(
      setTimeout(() => {
        element.style.transition = "background-color 300ms"
        // eslint-disable-next-line prefer-destructuring
        element.style.backgroundColor = theme.colors.primary[50]
      }, 400)
    )

    timeouts.push(
      setTimeout(() => {
        element.style.backgroundColor = ""
        if (onEnd) onEnd()
      }, 2500)
    )
  }

  return timeouts
}

export const getShareJobContent = (
  platform: "linkedin" | "whatsapp" | "facebook" | "telegram",
  {
    profile,
    job,
  }: {
    profile: Pick<
      SchoolProfile,
      "name" | "slug" | "branch" | "city" | "state" | "country"
    >
    job: { position: string; subject: string; slug: string }
  }
) => {
  const skipEmojis = platform === "facebook"
  const shareURL = `${window.location.origin}/school/${profile.slug}?jobSlug=${job.slug}`

  const renderEmoji = (emoji: string) => {
    if (skipEmojis) return ""
    return `${emoji} `
  }

  const heading = `${renderEmoji("👋")}Hi, we are hiring a ${job.position} at ${
    profile.name
  }${profile.branch ? ` (${profile.branch})` : ""}.`

  const locationChunks = [
    profile.city ?? "",
    profile.state?.name ?? "",
    profile.country?.name ?? "",
  ]

  const location = locationChunks.join(", ")

  switch (platform) {
    case "whatsapp":
    case "telegram": {
      return `${heading}

${renderEmoji("📖")}Subject: ${job.subject}
${
  locationChunks.filter(Boolean).length
    ? `${renderEmoji("📍")}Location: ${location}`
    : ""
}

Apply Here: ${shareURL}

Know someone who might fit the role? Please refer them to us ${renderEmoji(
        "👍"
      )}`
    }
    case "facebook": {
      return `${heading}

${renderEmoji("📖")}Subject: ${job.subject}
${
  locationChunks.filter(Boolean).length
    ? `${renderEmoji("📍")}Location: ${location}`
    : ""
}

Apply Here: ${shareURL}

Know someone who might fit the role? Please refer them to us ${renderEmoji(
        "👍"
      )}
#hiring #teacherrecruitment #teachingjobs #suraasajobs`
    }
    case "linkedin": {
      return `${heading}

${renderEmoji("📖")}Subject: ${job.subject}
${
  locationChunks.filter(Boolean).length
    ? `${renderEmoji("📍")}Location: ${location}`
    : ""
}
      
Apply Here: ${shareURL}
      
Know someone who might fit the role? Tag them in the comments! ${renderEmoji(
        "👍"
      )}
#hiring #teacherrecruitment #teachingjobs #suraasajobs`
    }
    default:
      return ""
  }
}

export const learningItemSlugs = {
  pgctl: "professional-graduate-certificate-in-teaching-learning",
  med: "masters-in-education",
}

export const buildUserName = (user: {
  firstName: string
  lastName: string | null
}) => [user.firstName, user.lastName].filter(Boolean).join(" ")

export const humaniseBytes = (bytes: number) => {
  const megabytes = bytes / (1024 * 1024)
  const formattedMB =
    megabytes % 1 === 0 ? megabytes.toFixed(0) : megabytes.toFixed(1)

  if (megabytes < 1) {
    return `${(megabytes * 1024).toFixed(0)}KB`
  }

  return `${formattedMB}MB`
}

export const MBToBytes = (mb: number) => mb * 1000000

export const signInAndContinue = (redirect = true) => {
  const baseUrl = getPlatformURL("sso", `/`)
  const url = new URL(baseUrl)

  const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const redirectURL = `${
    window.location.origin + routes.sso
  }?next=${encodeURIComponent(next)}`

  const params = new URLSearchParams(window.location.search)
  const utmParams = {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
  }

  url.searchParams.set("platform", USER_TYPE)
  url.searchParams.set("redirect-url", redirectURL)

  if (utmParams.utm_source)
    url.searchParams.set("utm_source", utmParams.utm_source)
  if (utmParams.utm_medium)
    url.searchParams.set("utm_medium", utmParams.utm_medium)
  if (utmParams.utm_campaign)
    url.searchParams.set("utm_campaign", utmParams.utm_campaign)
  if (utmParams.utm_term) url.searchParams.set("utm_term", utmParams.utm_term)
  if (utmParams.utm_content)
    url.searchParams.set("utm_content", utmParams.utm_content)

  if (redirect) window.location.href = url.toString()

  return url.toString()
}

export const getPhoneNumber = (phoneNumber: PhoneNumber) =>
  [
    typeof phoneNumber.code === "string"
      ? phoneNumber.code
      : `+${phoneNumber.code}`,
    phoneNumber.number,
  ]
    .filter(Boolean)
    .join("")

export const getCoverImageURL = (style?: CoverStyle, isXs = false) => {
  if (!style) {
    return undefined
  }

  let baseUrl = "https://assets.suraasa.com/lms/profile-assets/cover-images/"

  if (isXs) {
    baseUrl += "mobile/"
  }

  return `${baseUrl}${style}.webp`
}
